import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { produce } from 'immer';
import type { Model, ModelVersion, Checkpoint, Feature, ModelOperation } from '../types/Model';
import type { ApiServices } from '../hooks/useApi';
import type { ModelCreateRequest, CheckpointRequest, FeatureRequest } from '../services/api/model';
import type {
  ModelOperationProgressData,
  ModelOperationResultData,
  ModelPublishedData,
} from '../services/socket/types';
import { useProfileStore, PREFERENCE_KEYS } from './profileStore';
import { stripLeadingArticle } from '../utils/formatHelpers';
import { getLogger } from '../utils/logger';
import { retryUntilCondition } from '../utils/apiRetryUtils';

// Socket message data types for model store handlers
interface ModelCreatedMessage { modelId: string }
interface ModelDeletedMessage { modelId: string }
interface ModelChangeMessage { modelId: string; name?: string; description?: string; handle?: string; picture?: string; visibility?: string }
interface ModelVersionCreatedMessage { modelId: string; versionId: string }
interface ModelVersionDeletedMessage { modelId: string; versionId: string }
interface CheckpointCreatedMessage { modelId: string; versionId: string; checkpointId: string }

const logger = getLogger('modelStore');

/**
 * ModelStoreV2 - Simplified, lab-scoped model store with CRUD operations
 *
 * Key improvements over modelStore:
 * - Single lab storage (clears on lab switch)
 * - No polling (manual refresh only)
 * - Optimistic updates with rollback
 * - Simplified loading states
 * - Two main API calls for data loading
 * - ~500 lines instead of ~1600 lines
 */

// Enhanced loading state with granular tracking
interface LoadingState {
  models: boolean;
  modelDetails: Record<string, boolean>;
  creating: boolean;
  updating: Record<string, boolean>;
  deleting: Record<string, boolean>;
  featureOps: Record<string, boolean>;
}

interface ErrorState {
  models: Error | null;
  modelDetails: Record<string, Error | null>;
}

interface ModelStoreState {
  // Lab-scoped storage - only stores current lab's models
  labId: string | null;
  models: Map<string, Model>; // modelId -> Model

  // Current selection (URL-driven)
  currentModelId: string | null;

  // Enhanced loading states
  loading: LoadingState;

  // Error states
  errors: ErrorState;

  // UI state for models (not persisted to server)
  modelUIState: Map<string, { canceling: boolean }>; // Map<modelId, UIState>

  // Attention indicators for models with changes while not viewing (memory-only)
  modelAttentionSet: Set<string>;
}

interface ModelStoreActions {
  // Core data loading
  loadModelsForLab: (api: ApiServices, labId: string) => Promise<void>;
  loadModelDetails: (api: ApiServices, modelId: string, silent?: boolean) => Promise<void>;

  // Lab management
  setCurrentLab: (labId: string) => void;
  clearLab: () => void;

  // Model selection
  setCurrentModel: (modelId: string | null) => void;

  // Attention indicators
  addModelAttention: (modelId: string) => void;

  // Model CRUD operations
  createModel: (api: ApiServices, data: ModelCreateRequest) => Promise<Model | null>;
  updateModel: (api: ApiServices, modelId: string, data: Partial<Model>) => Promise<Model | null>;
  updateModelStatus: (api: ApiServices, modelId: string, status: 'active' | 'archived' | 'disabled') => Promise<Model | null>;
  deleteModel: (api: ApiServices, modelId: string) => Promise<boolean>;
  publishModel: (api: ApiServices, modelId: string, visibility: string, bettingStrategy?: string) => Promise<void>;

  // Version operations
  createModelVersion: (api: ApiServices, modelId: string, features: Feature[]) => Promise<ModelVersion | null>;
  deleteModelVersion: (api: ApiServices, modelId: string, versionId: string) => Promise<void>;

  // Checkpoint operations
  createCheckpoint: (api: ApiServices, modelId: string, data: CheckpointRequest) => Promise<Checkpoint | null>;
  cancelOperation: (api: ApiServices, modelId: string, versionId: string, checkpointId: string) => Promise<void>;
  loadVersionCheckpoints: (api: ApiServices, modelId: string, versionId: string) => Promise<void>;

  // Feature CRUD operations
  createFeature: (api: ApiServices, modelId: string, feature: FeatureRequest) => Promise<Feature | null>;
  updateFeature: (api: ApiServices, modelId: string, featureId: string, feature: FeatureRequest) => Promise<Feature | null>;
  deleteFeature: (api: ApiServices, modelId: string, featureId: string) => Promise<boolean>;
  importFeatures: (api: ApiServices, modelId: string, features: Partial<Feature>[]) => Promise<void>;

  // Selectors
  getModel: (modelId: string) => Model | undefined;
  getCurrentModel: () => Model | undefined;
  getModelsArray: () => Model[];
  getModelsByLab: (labId: string) => Model[];

  // Computed helpers
  getLatestVersion: (model: Model) => ModelVersion | undefined;
  getLatestCheckpoint: (model: Model) => Checkpoint | undefined;
  isProcessing: (model: Model) => boolean;
  hasUnpublishedChanges: (model: Model) => boolean;

  // Utility methods
  isModelBusy: (modelId: string) => boolean;
  isModelCanceling: (modelId: string) => boolean;
  isLoadingVersionCheckpoints: (modelId: string, versionId: string) => boolean;
  refreshModel: (api: ApiServices, modelId: string, silent?: boolean) => Promise<void>;
  refreshModels: (api: ApiServices) => Promise<void>;
  reset: () => void;

  // Version status management
  updateModelVersionStatus: (api: ApiServices, modelId: string, versionId: string, status: string) => Promise<void>;
  revertToCheckpoint: (api: ApiServices, modelId: string, checkpointId: string) => Promise<void>;

  // Socket message handlers
  handleModelOperationProgress: (api: ApiServices, message: ModelOperationProgressData) => Promise<void>;
  handleModelOperationResult: (api: ApiServices, message: ModelOperationResultData) => Promise<void>;
  handleModelCreated: (api: ApiServices, message: ModelCreatedMessage) => Promise<void>;
  handleModelDeleted: (api: ApiServices, message: ModelDeletedMessage) => Promise<void>;
  handleModelPublished: (api: ApiServices, message: ModelPublishedData) => Promise<void>;
  handleModelChange: (api: ApiServices, message: ModelChangeMessage) => Promise<void>;
  handleModelVersionCreated: (api: ApiServices, message: ModelVersionCreatedMessage) => Promise<void>;
  handleModelVersionDeleted: (api: ApiServices, message: ModelVersionDeletedMessage) => Promise<void>;
  handleCheckpointCreated: (api: ApiServices, message: CheckpointCreatedMessage) => Promise<void>;

  // Cache-only update methods (no UI state changes)
  updateModelInCache: (api: ApiServices, modelId: string) => Promise<void>;
  removeModelFromCache: (api: ApiServices, modelId: string) => Promise<void>;
  updateVersionInCache: (api: ApiServices, modelId: string, versionId: string) => Promise<void>;
  updateCheckpointInCache: (api: ApiServices, modelId: string, versionId: string, checkpointId: string) => Promise<void>;

  // Eventual consistency helpers
  waitForCheckpointStatus: (api: ApiServices, modelId: string, versionId: string, checkpointId: string, expectedStatus: string) => Promise<boolean>;
  waitForVersionStatus: (api: ApiServices, modelId: string, versionId: string, expectedStatuses: string[]) => Promise<boolean>;
  waitForModelExists: (api: ApiServices, modelId: string) => Promise<boolean>;
  waitForModelNotExists: (api: ApiServices, modelId: string) => Promise<boolean>;
  waitForModelVersionExists: (api: ApiServices, modelId: string, versionId: string) => Promise<boolean>;
  waitForModelVersionNotExists: (api: ApiServices, modelId: string, versionId: string) => Promise<boolean>;
  waitForCheckpointExists: (api: ApiServices, modelId: string, versionId: string, checkpointId: string) => Promise<boolean>;
}

const initialLoadingState: LoadingState = {
  models: false,
  modelDetails: {},
  creating: false,
  updating: {},
  deleting: {},
  featureOps: {},
};

const initialErrorState: ErrorState = {
  models: null,
  modelDetails: {},
};

// In-flight guard: deduplicate concurrent loadModelsForLab calls for the same lab
let modelsInFlightFor: string | null = null;
let modelsInFlightPromise: Promise<void> | null = null;

export const useModelStore = create<ModelStoreState & ModelStoreActions>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    labId: null,
    models: new Map(),
    currentModelId: null,
    loading: initialLoadingState,
    errors: initialErrorState,
    modelUIState: new Map(),
    modelAttentionSet: new Set(),

    // Load all models for a lab with their latest versions
    loadModelsForLab: async (api, labId) => {
      // If a fetch for this lab is already in-flight, reuse it
      if (modelsInFlightFor === labId && modelsInFlightPromise) {
        return modelsInFlightPromise;
      }

      const doFetch = async () => {
        set((state) => ({
          loading: { ...state.loading, models: true },
          errors: { ...state.errors, models: null },
        }));

        try {
          const response = await api.model.getLabModelsWithLatestVersion(labId);

          // Get existing models map to preserve detailed data
          const existingModels = get().models;
          const modelMap = new Map<string, Model>();

          const sortedModels = response.items.sort((a: Model, b: Model) =>
            stripLeadingArticle(a.name).localeCompare(stripLeadingArticle(b.name))
          );

          sortedModels.forEach((model: Model) => {
            const existingModel = existingModels.get(model.id);

            // SMART MERGE: Preserve checkpoint data if it exists
            // getLabModelsWithLatestVersion returns models without checkpoints,
            // but loadModelDetails loads models with checkpoints. We need to preserve
            // that detailed data to avoid race conditions.
            if (existingModel?.versions?.[0]?.checkpoints && existingModel.versions[0].checkpoints.length > 0) {
              // Existing model has detailed checkpoint data - preserve it
              // Only update top-level fields and version status that might have changed
              logger.debug('[ModelStore] Preserving checkpoint data for model:', {
                modelId: model.id,
                existingCheckpointCount: existingModel.versions[0].checkpoints.length,
                existingVersionStatus: existingModel.versions[0].status,
                newVersionStatus: model.versions?.[0]?.status
              });

              modelMap.set(model.id, {
                ...existingModel,
                // Update top-level fields that might have changed
                name: model.name,
                description: model.description,
                handle: model.handle,
                picture: model.picture,
                visibility: model.visibility,
                lastUpdated: model.lastUpdated,
                // If new model has version data, merge status but keep checkpoints and features
                versions: model.versions?.length ? [{
                  ...existingModel.versions[0],
                  status: model.versions[0].status, // Update status from server
                  lastUpdated: model.versions[0].lastUpdated, // Update timestamp
                  // Keep existing checkpoints and features from detailed load
                }] : existingModel.versions
              });
            } else {
              // No detailed data exists, use the new model as-is
              modelMap.set(model.id, model);
            }
          });

          set((state) => ({
            labId,
            models: modelMap,
            loading: { ...state.loading, models: false },
          }));
        } catch (error) {
          set((state) => ({
            loading: { ...state.loading, models: false },
            errors: { ...state.errors, models: error as Error },
          }));
          throw error;
        } finally {
          modelsInFlightFor = null;
          modelsInFlightPromise = null;
        }
      };

      modelsInFlightFor = labId;
      modelsInFlightPromise = doFetch();
      return modelsInFlightPromise;
    },

    // Load full details for a specific model (all versions + latest checkpoints)
    loadModelDetails: async (api, modelId, silent = false) => {
      // Auto-set lab from profileStore if not already set
      const currentLabId = get().labId;
      const selectedLab = useProfileStore.getState().getSelectedLab();
      const preservedModelId = get().currentModelId;

      if (!currentLabId && selectedLab) {
        // Temporarily preserve currentModelId when setting lab for the first time
        get().setCurrentLab(selectedLab.id);
        // Restore currentModelId after setCurrentLab cleared it
        if (preservedModelId) {
          set({ currentModelId: preservedModelId });
        }
      } else if (!currentLabId && !selectedLab) {
        console.warn('loadModelDetails: No lab set');
        return;
      }

      // Only set loading state if not silent
      if (!silent) {
        set((state) => ({
          loading: {
            ...state.loading,
            modelDetails: { ...state.loading.modelDetails, [modelId]: true },
          },
          errors: {
            ...state.errors,
            modelDetails: { ...state.errors.modelDetails, [modelId]: null },
          },
        }));
      }

      try {
        const model = await api.model.getModelWithLatestCheckpoints(modelId);

        if (!model) {
          throw new Error('Model not found');
        }

        // DIAGNOSTIC: Log checkpoint data to help identify missing checkpoints issue
        logger.info('[ModelStore] loadModelDetails result:', {
          modelId,
          hasVersions: !!model.versions,
          versionCount: model.versions?.length || 0,
          latestVersionId: model.versions?.[0]?.id,
          latestVersionStatus: model.versions?.[0]?.status,
          hasCheckpoints: !!model.versions?.[0]?.checkpoints,
          checkpointCount: model.versions?.[0]?.checkpoints?.length || 0,
          checkpointIds: model.versions?.[0]?.checkpoints?.map((cp: Checkpoint) => cp.id).slice(0, 3) || []
        });

        set((state) => {
          const newModels = new Map(state.models);
          newModels.set(modelId, model);

          return {
            models: newModels,
            // Only clear loading state if we set it
            ...(silent ? {} : {
              loading: {
                ...state.loading,
                modelDetails: { ...state.loading.modelDetails, [modelId]: false },
              },
            }),
          };
        });
      } catch (error) {
        console.error('[ModelStore] Error loading model details:', error);

        // Only update loading/error state if not silent
        if (!silent) {
          set((state) => ({
            loading: {
              ...state.loading,
              modelDetails: { ...state.loading.modelDetails, [modelId]: false },
            },
            errors: {
              ...state.errors,
              modelDetails: { ...state.errors.modelDetails, [modelId]: error as Error },
            },
          }));
        }
        throw error;
      }
    },

    // Lab management
    setCurrentLab: (labId) => {
      const currentLabId = get().labId;

      if (currentLabId !== labId) {
        set({
          labId,
          models: new Map(),
          currentModelId: null,
          loading: { ...initialLoadingState, models: true },
          errors: initialErrorState,
          modelUIState: new Map(),
          modelAttentionSet: new Set(),
        });
      }
    },

    clearLab: () => {
      set({
        labId: null,
        models: new Map(),
        currentModelId: null,
        loading: initialLoadingState,
        errors: initialErrorState,
        modelUIState: new Map(),
        modelAttentionSet: new Set(),
      });
    },

    // Model selection
    setCurrentModel: (modelId) => {
      if (modelId) {
        // Clear attention indicator when model is selected
        const newAttentionSet = new Set(get().modelAttentionSet);
        newAttentionSet.delete(modelId);
        set({ currentModelId: modelId, modelAttentionSet: newAttentionSet });
      } else {
        set({ currentModelId: modelId });
      }
    },

    // Attention indicators
    addModelAttention: (modelId) => {
      const newAttentionSet = new Set(get().modelAttentionSet);
      newAttentionSet.add(modelId);
      set({ modelAttentionSet: newAttentionSet });
    },

    // Model CRUD Operations

    createModel: async (api, data) => {
      set((state) => ({ loading: { ...state.loading, creating: true } }));

      try {
        const model = await api.model.createModel(data);
        if (model) {
          set((state) => ({
            models: new Map(state.models).set(model.id, model),
            loading: { ...state.loading, creating: false },
          }));

          // If this is the first model, select it
          if (get().models.size === 1) {
            set({ currentModelId: model.id });
          }
        }
        return model;
      } catch (error) {
        set((state) => ({ loading: { ...state.loading, creating: false } }));
        throw error;
      }
    },

    updateModel: async (api, modelId, data) => {
      const original = get().models.get(modelId);
      if (!original) return null;

      // Optimistic update
      const optimistic = { ...original, ...data };
      set((state) => ({
        models: new Map(state.models).set(modelId, optimistic),
        loading: { ...state.loading, updating: { ...state.loading.updating, [modelId]: true } },
      }));

      try {
        const verified = await api.model.updateModel({ id: modelId, ...data });

        // SMART MERGE: Sync with verified response but preserve checkpoint data
        // The API returns only metadata, but we want to keep versions with their checkpoints
        if (verified) {
          const mergedModel = produce(original, (draft) => {
            // Update top-level metadata fields from verified response
            draft.name = verified.name;
            draft.description = verified.description;
            draft.handle = verified.handle;
            draft.picture = verified.picture;
            draft.visibility = verified.visibility;
            draft.lastUpdated = verified.lastUpdated;

            // If verified has version data, update version status but preserve checkpoints and features
            if (verified.versions?.length && draft.versions?.[0]) {
              draft.versions[0].status = verified.versions[0].status;
              draft.versions[0].lastUpdated = verified.versions[0].lastUpdated;
              // Keep existing checkpoints and features from original
            }
          });

          set((state) => ({
            models: new Map(state.models).set(modelId, mergedModel),
            loading: { ...state.loading, updating: { ...state.loading.updating, [modelId]: false } },
          }));
          return mergedModel;
        }
        return optimistic;
      } catch (error) {
        // Rollback
        set((state) => ({
          models: new Map(state.models).set(modelId, original),
          loading: { ...state.loading, updating: { ...state.loading.updating, [modelId]: false } },
        }));
        throw error;
      }
    },

    updateModelStatus: async (api, modelId, status) => {
      const original = get().models.get(modelId);
      if (!original) return null;

      // Optimistic update
      const optimistic = { ...original, status };
      set((state) => ({
        models: new Map(state.models).set(modelId, optimistic),
        loading: { ...state.loading, updating: { ...state.loading.updating, [modelId]: true } },
      }));

      try {
        const verified = await api.model.updateModelStatus(modelId, status);

        if (verified) {
          set((state) => ({
            models: new Map(state.models).set(modelId, { ...original, ...verified }),
            loading: { ...state.loading, updating: { ...state.loading.updating, [modelId]: false } },
          }));
          return verified;
        }
        return optimistic;
      } catch (error) {
        // Rollback
        set((state) => ({
          models: new Map(state.models).set(modelId, original),
          loading: { ...state.loading, updating: { ...state.loading.updating, [modelId]: false } },
        }));
        throw error;
      }
    },

    deleteModel: async (api, modelId) => {
      const model = get().models.get(modelId);
      if (!model) return false;

      // Optimistic delete
      // set((state) => {
      //   const newModels = new Map(state.models);
      //   newModels.delete(modelId);
      //   return {
      //     models: newModels,
      //     currentModelId: state.currentModelId === modelId ? null : state.currentModelId,
      //     loading: { ...state.loading, deleting: { ...state.loading.deleting, [modelId]: true } },
      //   };
      // });

      try {
        const success = await api.model.deleteModel(modelId);
        if (!success) {
          // Restore if API couldn't verify deletion
          set((state) => ({ models: new Map(state.models).set(modelId, model) }));
        }
        return success;
      } catch (error) {
        // Restore on error
        set((state) => ({ models: new Map(state.models).set(modelId, model) }));
        throw error;
      } finally {
        set((state) => ({
          loading: { ...state.loading, deleting: { ...state.loading.deleting, [modelId]: false } },
        }));
      }
    },

    publishModel: async (api, modelId, visibility, bettingStrategy?) => {
      const model = get().models.get(modelId);
      if (!model) return;

      // Optimistic update
      const updatedModel = produce(model, (draft) => {
        draft.visibility = visibility as Model['visibility'];
        if (draft.versions?.[0]) {
          draft.versions[0].status = 'published';
        }
      });

      set((state) => ({ models: new Map(state.models).set(modelId, updatedModel) }));

      try {
        await api.model.publishModel(modelId, visibility, bettingStrategy);
        // Refresh to get server state
        await get().loadModelDetails(api, modelId);
      } catch (error) {
        // Rollback
        set((state) => ({ models: new Map(state.models).set(modelId, model) }));
        throw error;
      }
    },

    // Version Operations

    createModelVersion: async (api, modelId, features) => {
      try {
        const version = await api.model.createModelVersion(modelId, features);

        if (version) {
          // Refresh model to get new version
          await get().loadModelDetails(api, modelId);
        }
        return version;
      } catch (error) {
        console.error('[ModelStore] Error creating model version:', error);
        throw error;
      }
    },

    deleteModelVersion: async (api, modelId, versionId) => {
      const model = get().models.get(modelId);
      if (!model?.versions) return;

      const versionIndex = model.versions.findIndex((v) => v.id === versionId);
      if (versionIndex === -1) return;

      try {
        const success = await api.model.deleteModelVersion(modelId, versionId);
        if (success) {
          // Remove version from model
          const updatedModel = produce(model, (draft) => {
            if (draft.versions) {
              draft.versions.splice(versionIndex, 1);
            }
          });

          set((state) => ({ models: new Map(state.models).set(modelId, updatedModel) }));
        }
      } catch (error) {
        // Rollback
        set((state) => ({ models: new Map(state.models).set(modelId, model) }));
        throw error;
      }
    },

    // Checkpoint Operations

    createCheckpoint: async (api, modelId, data) => {
      const model = get().models.get(modelId);
      if (!model) return null;

      set((state) => ({
        loading: { ...state.loading, featureOps: { ...state.loading.featureOps, [modelId]: true } },
      }));

      try {
        const checkpoint = await api.model.createCheckpoint(modelId, data);

        if (checkpoint) {
          // Update model with new checkpoint
          const updatedModel = produce(model, (draft) => {
            if (draft.versions?.[0]) {
              if (!draft.versions[0].checkpoints) {
                draft.versions[0].checkpoints = [];
              }
              draft.versions[0].checkpoints.unshift(checkpoint);
              draft.versions[0].status = checkpoint.type === 'train' ? 'training' : 'experimenting';
            }
          });

          set((state) => ({ models: new Map(state.models).set(modelId, updatedModel) }));
        }

        return checkpoint;
      } finally {
        set((state) => ({
          loading: { ...state.loading, featureOps: { ...state.loading.featureOps, [modelId]: false } },
        }));
      }
    },

    cancelOperation: async (api, modelId, versionId, checkpointId) => {
      try {
        // Set canceling state
        set((state) => {
          const newUIState = new Map(state.modelUIState);
          newUIState.set(modelId, { canceling: true });
          return { modelUIState: newUIState };
        });

        await api.model.cancelOperation(modelId, versionId, checkpointId);

      } catch (error) {
        // Clear canceling state on error
        set((state) => {
          const newUIState = new Map(state.modelUIState);
          newUIState.set(modelId, { canceling: false });
          return { modelUIState: newUIState };
        });
        throw error;
      }
    },

    loadVersionCheckpoints: async (api, modelId, versionId) => {
      const model = get().models.get(modelId);
      if (!model) return;

      // Set loading state for this specific version
      const loadingKey = `${modelId}:${versionId}`;
      set((state) => ({
        loading: {
          ...state.loading,
          modelDetails: {
            ...state.loading.modelDetails,
            [loadingKey]: true,
          },
        },
      }));

      try {
        // Call the API to get checkpoints for this version
        const checkpoints = await api.model.getCheckpoints(modelId, versionId);

        // Update the model with the loaded checkpoints
        const updatedModel = produce(model, (draft) => {
          const versionIndex = draft.versions?.findIndex((v) => v.id === versionId);
          if (versionIndex !== undefined && versionIndex !== -1 && draft.versions) {
            draft.versions[versionIndex].checkpoints = checkpoints;
          }
        });

        set((state) => ({
          models: new Map(state.models).set(modelId, updatedModel),
          loading: {
            ...state.loading,
            modelDetails: {
              ...state.loading.modelDetails,
              [loadingKey]: false,
            },
          },
        }));
      } catch (error) {
        console.error('[ModelStore] Error loading version checkpoints:', error);
        set((state) => ({
          loading: {
            ...state.loading,
            modelDetails: {
              ...state.loading.modelDetails,
              [loadingKey]: false,
            },
          },
        }));
        throw error;
      }
    },

    // Feature CRUD Operations

    createFeature: async (api, modelId, feature) => {
      const model = get().models.get(modelId);
      if (!model?.versions?.[0]) return null;

      const newFeature = await api.model.createFeature(modelId, feature);
      if (newFeature) {
        const updatedModel = produce(model, (draft) => {
          if (draft.versions?.[0]) {
            if (!draft.versions[0].features) {
              draft.versions[0].features = [];
            }
            draft.versions[0].features.push(newFeature);
          }
        });

        set((state) => ({ models: new Map(state.models).set(modelId, updatedModel) }));
      }
      return newFeature;
    },

    updateFeature: async (api, modelId, featureId, feature) => {
      const model = get().models.get(modelId);
      if (!model?.versions?.[0]?.features) return null;

      const featureIndex = model.versions[0].features.findIndex((f) => f.featureId === featureId);
      if (featureIndex === -1) return null;

      // Optimistic update with Immer
      const updatedModel = produce(model, (draft) => {
        if (draft.versions?.[0]?.features) {
          draft.versions[0].features[featureIndex] = {
            ...draft.versions[0].features[featureIndex],
            ...feature,
          };
        }
      });

      set((state) => ({
        models: new Map(state.models).set(modelId, updatedModel),
        loading: { ...state.loading, featureOps: { ...state.loading.featureOps, [modelId]: true } },
      }));

      try {
        const updatedFeature = await api.model.updateFeature(modelId, featureId, feature);

        // Sync with API response if available
        if (updatedFeature) {
          const finalModel = produce(model, (draft) => {
            if (draft.versions?.[0]?.features) {
              draft.versions[0].features[featureIndex] = updatedFeature;
            }
          });
          set((state) => ({ models: new Map(state.models).set(modelId, finalModel) }));
        }

        return updatedFeature;
      } catch (error) {
        // Rollback
        set((state) => ({ models: new Map(state.models).set(modelId, model) }));
        throw error;
      } finally {
        set((state) => ({
          loading: { ...state.loading, featureOps: { ...state.loading.featureOps, [modelId]: false } },
        }));
      }
    },

    deleteFeature: async (api, modelId, featureId) => {
      const model = get().models.get(modelId);
      if (!model?.versions?.[0]?.features) {
        return false;
      }

      const featureIndex = model.versions[0].features.findIndex((f) => f.featureId === featureId);
      if (featureIndex === -1) {
        return false;
      }

      // Optimistic delete
      const updatedModel = produce(model, (draft) => {
        if (draft.versions?.[0]?.features) {
          draft.versions[0].features.splice(featureIndex, 1);
        }
      });

      // Update store
      set((state) => {
        const newModels = new Map(state.models);
        newModels.set(modelId, updatedModel);
        return { models: newModels };
      });

      try {
        const success = await api.model.deleteFeature(modelId, featureId);
        if (!success) {
          // Restore if failed
          set((state) => ({ models: new Map(state.models).set(modelId, model) }));
        }
        return success;
      } catch (error) {
        // Rollback
        set((state) => ({ models: new Map(state.models).set(modelId, model) }));
        throw error;
      }
    },

    importFeatures: async (api, modelId, features) => {
      const model = get().models.get(modelId);
      if (!model?.versions?.[0]) {
        return;
      }

      // Ensure model is in draft mode before importing features
      const latestVersion = model.versions[0];
      if (latestVersion.status !== 'draft') {
        // Wait for status change to complete before proceeding with feature creation
        await get().updateModelVersionStatus(api, modelId, latestVersion.id, 'draft');
      }

      set((state) => ({
        loading: { ...state.loading, featureOps: { ...state.loading.featureOps, [modelId]: true } },
      }));

      try {
        // Import features sequentially to avoid race conditions
        const importedFeatures: Feature[] = [];
        for (const feature of features) {
          if (!feature.name || !feature.formula) continue;
          try {
            const newFeature = await api.model.createFeature(modelId, { name: feature.name, formula: feature.formula, description: feature.description });
            if (newFeature) {
              importedFeatures.push(newFeature);
            }
          } catch {
            // Continue with other features even if one fails
          }
        }

        // Update model with all imported features
        if (importedFeatures.length > 0) {
          const updatedModel = produce(model, (draft) => {
            if (draft.versions?.[0]) {
              if (!draft.versions[0].features) {
                draft.versions[0].features = [];
              }
              draft.versions[0].features.push(...importedFeatures);
            }
          });

          set((state) => ({ models: new Map(state.models).set(modelId, updatedModel) }));
        }
      } finally {
        set((state) => ({
          loading: { ...state.loading, featureOps: { ...state.loading.featureOps, [modelId]: false } },
        }));
      }
    },

    // Selectors
    getModel: (modelId) => {
      return get().models.get(modelId);
    },

    getCurrentModel: () => {
      const modelId = get().currentModelId;
      if (!modelId) {
        return undefined;
      }

      return get().models.get(modelId);
    },

    getModelsArray: () => {
      const models = Array.from(get().models.values());
      return models.sort((a, b) =>
        stripLeadingArticle(a.name).localeCompare(stripLeadingArticle(b.name))
      );
    },

    getModelsByLab: (labId) => {
      const currentLabId = get().labId;

      if (currentLabId === labId) {
        return get().getModelsArray();
      }

      return [];
    },

    // Computed helpers
    getLatestVersion: (model) => {
      return model.versions?.[0];
    },

    getLatestCheckpoint: (model) => {
      const latestVersion = get().getLatestVersion(model);
      return latestVersion?.checkpoints?.[0];
    },

    isProcessing: (model) => {
      const latestVersion = get().getLatestVersion(model);
      return ['experimenting', 'training'].includes(latestVersion?.status || '');
    },

    hasUnpublishedChanges: (model) => {
      const latestVersion = get().getLatestVersion(model);
      return !!latestVersion && latestVersion.status !== 'published';
    },

    // Utility methods
    isModelBusy: (modelId) => {
      const loading = get().loading;
      return (
        loading.updating[modelId] ||
        loading.deleting[modelId] ||
        loading.featureOps[modelId] ||
        false
      );
    },

    isLoadingVersionCheckpoints: (modelId, versionId) => {
      const loading = get().loading;
      const loadingKey = `${modelId}:${versionId}`;
      return loading.modelDetails[loadingKey] || false;
    },

    refreshModel: async (api, modelId, silent = false) => {
      await get().loadModelDetails(api, modelId, silent);
    },

    refreshModels: async (api) => {
      const labId = get().labId;
      if (labId) {
        await get().loadModelsForLab(api, labId);
      }
    },

    // Version status management
    updateModelVersionStatus: async (api, modelId, versionId, status) => {
      const selectedLab = useProfileStore.getState().getSelectedLab();
      if (!selectedLab) return;

      const originalModel = get().getModel(modelId);
      if (!originalModel) return;

      // Find the original version for rollback
      const originalVersion = originalModel.versions?.find((v) => v.id === versionId);
      if (!originalVersion) return;

      // Optimistic update
      const updatedModel = produce(originalModel, (draft) => {
        const versionIndex = draft.versions?.findIndex((v) => v.id === versionId);
        if (versionIndex !== undefined && versionIndex !== -1 && draft.versions) {
          draft.versions[versionIndex].status = status as ModelVersion['status'];
        }
      });

      set((state) => ({
        models: new Map(state.models).set(modelId, updatedModel),
      }));

      try {
        await api.model.updateModelVersion(modelId, versionId, { status });

        // Wait for the status update to be indexed in OpenSearch before refreshing
        const statusUpdated = await get().waitForVersionStatus(api, modelId, versionId, [status]);

        if (!statusUpdated) {
          logger.warn('[ModelStore] Version status update not confirmed after retries, proceeding anyway');
        }

        // Refresh to get server state (silent to avoid loading flicker since we already optimistically updated)
        await get().refreshModel(api, modelId, true);
      } catch (error) {
        // Rollback on error
        set((state) => ({
          models: new Map(state.models).set(modelId, originalModel),
        }));
        throw error;
      }
    },

    revertToCheckpoint: async (api, modelId, checkpointId) => {
      const model = get().getModel(modelId);
      const latestVersion = model ? get().getLatestVersion(model) : null;

      if (!model?.id || !latestVersion) {
        throw new Error('Model or version not found');
      }

      // Find the checkpoint
      let targetCheckpoint: Checkpoint | undefined;
      for (const version of model.versions || []) {
        targetCheckpoint = version.checkpoints?.find((cp) => cp.id === checkpointId);
        if (targetCheckpoint) break;
      }

      if (!targetCheckpoint) {
        throw new Error('Checkpoint not found');
      }

      // Get current features and checkpoint features
      const currentFeatures = latestVersion.features || [];
      const checkpointFeatures = targetCheckpoint.features || [];

      try {
        // Set to draft if not already
        if (
          latestVersion.status !== 'draft' &&
          latestVersion.status !== 'experimenting' &&
          latestVersion.status !== 'training'
        ) {
          await get().updateModelVersionStatus(api, model.id, latestVersion.id, 'draft');
        }

        // Delete all current features
        for (const feature of currentFeatures) {
          await get().deleteFeature(api, model.id, feature.featureId);
        }

        // Import checkpoint features
        await get().importFeatures(api, model.id, checkpointFeatures);

        // Refresh the model to ensure we have the latest state (silent to avoid loading flicker since we already imported features)
        await get().refreshModel(api, modelId, true);
      } catch (error) {
        console.error('Error reverting to checkpoint:', error);
        throw error;
      }
    },

    isModelCanceling: (modelId) => {
      return get().modelUIState.get(modelId)?.canceling || false;
    },

    reset: () => {
      set({
        labId: null,
        models: new Map(),
        currentModelId: null,
        loading: initialLoadingState,
        errors: initialErrorState,
        modelUIState: new Map(),
        modelAttentionSet: new Set(),
      });
    },

    // Cache-only update methods (no UI state changes)
    updateModelInCache: async (api, modelId) => {
      try {
        const model = await api.model.getModelWithLatestCheckpoints(modelId);
        if (model) {
          // DIAGNOSTIC: Log checkpoint data to help identify missing checkpoints issue
          logger.info('[ModelStore] updateModelInCache result:', {
            modelId,
            hasVersions: !!model.versions,
            versionCount: model.versions?.length || 0,
            latestVersionId: model.versions?.[0]?.id,
            latestVersionStatus: model.versions?.[0]?.status,
            hasCheckpoints: !!model.versions?.[0]?.checkpoints,
            checkpointCount: model.versions?.[0]?.checkpoints?.length || 0,
            checkpointIds: model.versions?.[0]?.checkpoints?.map((cp: Checkpoint) => cp.id).slice(0, 3) || []
          });

          set((state) => ({
            models: new Map(state.models).set(modelId, model),
          }));
        }
      } catch (error) {
        logger.error('[ModelStore] Failed to update model in cache:', error);
      }
    },

    removeModelFromCache: async (api, modelId) => {
      try {
        set((state) => {
          const newModels = new Map(state.models);
          newModels.delete(modelId);
          return { models: newModels };
        });
      } catch (error) {
        logger.error('[ModelStore] Failed to remove model from cache:', error);
      }
    },

    updateVersionInCache: async (api, modelId, versionId) => {
      try {
        const version = await api.model.getModelVersion(modelId, versionId, { includeCheckpoints: true });
        if (version) {
          const model = get().models.get(modelId);
          if (model) {
            const updatedModel = produce(model, (draft) => {
              // Check if version already exists
              const existingIndex = draft.versions?.findIndex(v => v.id === versionId);
              if (existingIndex !== undefined && existingIndex >= 0 && draft.versions) {
                // Update existing version at its current position
                draft.versions[existingIndex] = version;
              } else {
                // New version - insert at index 0 (it's the latest)
                if (!draft.versions) draft.versions = [];
                draft.versions.unshift(version);
              }
            });
            set((state) => ({
              models: new Map(state.models).set(modelId, updatedModel),
            }));
          }
        }
      } catch (error) {
        logger.error('[ModelStore] Failed to fetch version:', error);
      }
    },

    updateCheckpointInCache: async (api, modelId, versionId, checkpointId) => {
      try {
        const checkpoint = await api.model.getCheckpoint(modelId, versionId, checkpointId);
        if (checkpoint) {
          const model = get().models.get(modelId);
          if (model) {
            const updatedModel = produce(model, (draft) => {
              const version = draft.versions?.find(v => v.id === versionId);
              if (version) {
                if (!version.checkpoints) {
                  version.checkpoints = [];
                }
                // Check if checkpoint already exists
                const existingIndex = version.checkpoints.findIndex(cp => cp.id === checkpointId);
                if (existingIndex >= 0) {
                  // Update existing checkpoint at its current position
                  version.checkpoints[existingIndex] = checkpoint;
                } else {
                  // New checkpoint - insert at index 0 (newest first)
                  version.checkpoints.unshift(checkpoint);
                }
              }
            });
            set((state) => ({
              models: new Map(state.models).set(modelId, updatedModel),
            }));
          }
        }
      } catch (error) {
        logger.error('[ModelStore] Failed to fetch checkpoint:', error);
      }
    },

    // Eventual consistency helpers
    waitForCheckpointStatus: async (api, modelId, versionId, checkpointId, expectedStatus) => {
      logger.debug('[ModelStore] Waiting for checkpoint status:', {
        checkpointId,
        expectedStatus
      });

      const result = await retryUntilCondition(
        async () => {
          try {
            const checkpoint = await api.model.getCheckpoint(modelId, versionId, checkpointId);
            return checkpoint;
          } catch {
            logger.debug('[ModelStore] Checkpoint not found yet:', checkpointId);
            return null;
          }
        },
        (checkpoint) => {
          if (!checkpoint) return false;
          const matches = checkpoint.operation?.status === expectedStatus;
          logger.debug('[ModelStore] Checkpoint status check:', {
            checkpointId,
            currentStatus: checkpoint.operation?.status,
            expectedStatus,
            matches
          });
          return matches;
        },
        {
          maxRetries: 8, // More retries for eventual consistency
          delays: [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000], // Total ~18 seconds
          onRetry: (attempt, max) => {
            logger.debug(`[ModelStore] Waiting for checkpoint status (${attempt}/${max})`);
          }
        }
      );

      return result !== null;
    },

    waitForVersionStatus: async (api, modelId, versionId, expectedStatuses) => {
      logger.debug('[ModelStore] Waiting for version status:', {
        versionId,
        expectedStatuses
      });

      const result = await retryUntilCondition(
        async () => {
          try {
            const version = await api.model.getModelVersion(modelId, versionId);
            return version;
          } catch {
            logger.debug('[ModelStore] Version not found yet:', versionId);
            return null;
          }
        },
        (version) => {
          if (!version) return false;
          const matches = expectedStatuses.includes(version.status);
          logger.debug('[ModelStore] Version status check:', {
            versionId,
            currentStatus: version.status,
            expectedStatuses,
            matches
          });
          return matches;
        },
        {
          maxRetries: 10, // More retries since version status takes longer
          delays: [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 5000, 6000], // Total ~30 seconds
          onRetry: (attempt, max) => {
            logger.debug(`[ModelStore] Waiting for version status (${attempt}/${max})`);
          }
        }
      );

      return result !== null;
    },

    waitForModelExists: async (api, modelId) => {
      logger.debug('[ModelStore] Waiting for model to exist:', {
        modelId
      });
      logger.debug('[ModelStore] Starting creation confirmation polling - 404 responses are expected until model exists');

      const result = await retryUntilCondition(
        async () => {
          try {
            const model = await api.model.getModel(modelId, { suppressErrors: true });
            return model;
          } catch {
            logger.debug('[ModelStore] Model not found yet:', modelId);
            return null;
          }
        },
        (model) => {
          // Return true if model exists, false otherwise
          const exists = model !== null;
          logger.debug('[ModelStore] Model existence check:', {
            modelId,
            exists
          });
          return exists;
        },
        {
          onRetry: (attempt, max) => {
            logger.debug(`[ModelStore] Waiting for model to exist (${attempt}/${max})`);
          }
        }
      );

      return result !== null;
    },

    waitForModelNotExists: async (api, modelId) => {
      logger.debug('[ModelStore] Waiting for model to not exist:', {
        modelId
      });
      logger.debug('[ModelStore] Starting deletion confirmation polling - 404 responses are expected and indicate success');

      const result = await retryUntilCondition(
        async () => {
          try {
            const model = await api.model.getModel(modelId, { suppressErrors: true });
            return model;
          } catch {
            logger.debug('[ModelStore] Model not found (expected):', modelId);
            return null;
          }
        },
        (model) => {
          // Return true if model doesn't exist, false otherwise
          const notExists = model === null;
          logger.debug('[ModelStore] Model non-existence check:', {
            modelId,
            exists: !notExists
          });
          return notExists;
        },
        {
          onRetry: (attempt, max) => {
            logger.debug(`[ModelStore] Waiting for model to not exist (${attempt}/${max})`);
          }
        }
      );

      // Return true if model was successfully confirmed as deleted (result === null means condition was met)
      return result === null;
    },

    waitForModelVersionExists: async (api, modelId, versionId) => {
      logger.debug('[ModelStore] Waiting for model version to exist:', {
        modelId,
        versionId
      });
      logger.debug('[ModelStore] Starting version creation confirmation polling - 404 responses are expected until version exists');

      const result = await retryUntilCondition(
        async () => {
          try {
            const version = await api.model.getModelVersion(modelId, versionId, { suppressErrors: true });
            return version;
          } catch {
            logger.debug('[ModelStore] Model version not found yet:', { modelId, versionId });
            return null;
          }
        },
        (version) => {
          // Return true if version exists, false otherwise
          const exists = version !== null;
          logger.debug('[ModelStore] Model version existence check:', {
            modelId,
            versionId,
            exists
          });
          return exists;
        },
        {
          onRetry: (attempt, max) => {
            logger.debug(`[ModelStore] Waiting for model version to exist (${attempt}/${max})`);
          }
        }
      );

      return result !== null;
    },

    waitForModelVersionNotExists: async (api, modelId, versionId) => {
      logger.debug('[ModelStore] Waiting for model version to not exist:', {
        modelId,
        versionId
      });
      logger.debug('[ModelStore] Starting version deletion confirmation polling - 404 responses are expected and indicate success');

      const result = await retryUntilCondition(
        async () => {
          try {
            const version = await api.model.getModelVersion(modelId, versionId, { suppressErrors: true });
            return version;
          } catch {
            logger.debug('[ModelStore] Model version not found (expected):', { modelId, versionId });
            return null;
          }
        },
        (version) => {
          // Return true if version doesn't exist, false otherwise
          const notExists = version === null;
          logger.debug('[ModelStore] Model version non-existence check:', {
            modelId,
            versionId,
            exists: !notExists
          });
          return notExists;
        },
        {
          onRetry: (attempt, max) => {
            logger.debug(`[ModelStore] Waiting for model version to not exist (${attempt}/${max})`);
          }
        }
      );

      // Return true if version was successfully confirmed as deleted (result === null means condition was met)
      return result === null;
    },

    waitForCheckpointExists: async (api, modelId, versionId, checkpointId) => {
      logger.debug('[ModelStore] Waiting for checkpoint to exist:', {
        modelId,
        versionId,
        checkpointId
      });
      logger.debug('[ModelStore] Starting checkpoint creation confirmation polling - 404 responses are expected until checkpoint exists');

      const result = await retryUntilCondition(
        async () => {
          try {
            const checkpoint = await api.model.getCheckpoint(modelId, versionId, checkpointId, { suppressErrors: true });
            return checkpoint;
          } catch {
            logger.debug('[ModelStore] Checkpoint not found yet:', { modelId, versionId, checkpointId });
            return null;
          }
        },
        (checkpoint) => {
          // Return true if checkpoint exists, false otherwise
          const exists = checkpoint !== null;
          logger.debug('[ModelStore] Checkpoint existence check:', {
            modelId,
            versionId,
            checkpointId,
            exists
          });
          return exists;
        },
        {
          onRetry: (attempt, max) => {
            logger.debug(`[ModelStore] Waiting for checkpoint to exist (${attempt}/${max})`);
          }
        }
      );

      return result !== null;
    },

    // Socket message handlers
    handleModelOperationProgress: async (api, message) => {
      const { modelId, versionId, checkpointId, stage, percentComplete } = message;

      logger.debug('[ModelStore] Handling operation progress:', {
        modelId,
        versionId,
        checkpointId,
        stage,
        percentComplete,
      });

      // Step 1: Check if model exists in store
      const model = get().models.get(modelId);

      if (!model) {
        // Model not in store, fetch it and add to cache (no UI state change)
        logger.info('[ModelStore] Model not in store, fetching:', modelId);
        await get().updateModelInCache(api, modelId);
        return; // Done after loading
      }

      // Step 2: Look up the specific version by versionId (not assuming it's the latest)
      const version = model.versions?.find(v => v.id === versionId);

      if (!version) {
        // Version not found - it must be the new latest version, fetch and insert at index 0
        logger.info('[ModelStore] Version not found, fetching latest version:', {
          modelId,
          versionId,
        });
        await get().updateVersionInCache(api, modelId, versionId);
        return;
      }

      // Check if the version is in a processing state
      if (!['experimenting', 'training'].includes(version.status)) {
        // Version status is stale, refetch it
        logger.info('[ModelStore] Version not in processing state, reloading version:', {
          modelId,
          versionId,
          versionStatus: version.status,
        });
        await get().updateVersionInCache(api, modelId, versionId);
        return; // Done after reloading
      }

      // Step 3: Check if checkpoint exists
      const checkpoint = version.checkpoints?.find(cp => cp.id === checkpointId);

      if (!checkpoint) {
        // Checkpoint not found, fetch it
        logger.info('[ModelStore] Checkpoint not found, fetching:', {
          modelId,
          versionId,
          checkpointId,
        });
        await get().updateCheckpointInCache(api, modelId, versionId, checkpointId);
        return; // Done after fetching checkpoint
      }

      // Step 4: Update operation values locally
      logger.debug('[ModelStore] Updating checkpoint operation:', {
        checkpointId,
        stage,
        percentComplete,
      });

      const updatedModel = produce(model, (draft) => {
        const ver = draft.versions?.find(v => v.id === versionId);
        if (ver) {
          const cp = ver.checkpoints?.find(c => c.id === checkpointId);
          if (cp && cp.operation) {
            // Update operation fields - only update if values are defined
            if (stage !== undefined) {
              cp.operation.progress.currentStage = stage as ModelOperation['progress']['currentStage'];
            }
            if (percentComplete !== undefined) {
              cp.operation.progress.percentComplete = parseInt(percentComplete, 10);
            }
          }
        }
      });

      set((state) => ({
        models: new Map(state.models).set(modelId, updatedModel),
      }));

      logger.info('[ModelStore] Operation progress updated for checkpoint:', checkpointId);
    },

    handleModelOperationResult: async (api, message) => {
      const { modelId, versionId, checkpointId, status } = message;

      logger.debug('[ModelStore] Handling operation result:', {
        modelId,
        versionId,
        checkpointId,
        status
      });

      // Wait for version to exit processing states
      // Version should NOT be in experimenting or training state anymore
      const finalStatuses = ['draft', 'experimented', 'trained', 'published'];

      const versionReady = await get().waitForVersionStatus(
        api,
        modelId,
        versionId,
        finalStatuses  // Wait for any of these final states
      );

      if (!versionReady) {
        logger.warn('[ModelStore] Version still in processing state after retries:', {
          versionId,
          expectedStatuses: finalStatuses
        });
        // Continue anyway - we'll get the best data available
      } else {
        logger.info('[ModelStore] Version reached final state, proceeding with update');
      }

      // Check if model exists in store
      const model = get().models.get(modelId);

      if (!model) {
        // Model not in store, fetch it and add to cache (no UI state change)
        logger.info('[ModelStore] Model not in store, fetching:', modelId);
        await get().updateModelInCache(api, modelId);
      } else {
        // Model exists, refresh version only
        logger.info('[ModelStore] Model exists, refreshing version:', versionId);
        await get().updateVersionInCache(api, modelId, versionId);
      }

      // clear canceling locally if it was set
      set((state) => {
        const newUIState = new Map(state.modelUIState);
        newUIState.set(modelId, { canceling: false });
        return { modelUIState: newUIState };
      });

      // Add attention indicator if this model is not currently selected
      // Operation result means version reached experimented/trained state
      if (modelId !== get().currentModelId) {
        get().addModelAttention(modelId);
      }
    },

    handleModelCreated: async (api, message) => {
      const { modelId } = message;

      // look locally first, we might be the originator of the create
      const existing = get().models.get(modelId);
      if (existing) {
        logger.info('[ModelStore] Model already exists in store, skipping create handling:', {
          modelId,
        });
        return;
      }
      logger.debug('[ModelStore] Handling model created:', {
        modelId,
      });

      // Wait for model to exist

      const modelReady = await get().waitForModelExists(
        api,
        modelId
      );

      if (!modelReady) {
        logger.warn('[ModelStore] Model still not there after retries:', {
          modelId
        });
        // Continue anyway - we'll get the best data available
      } else {
        logger.info('[ModelStore] Model exists, proceeding with update');
      }

      // Check if model exists in store
      const model = get().models.get(modelId);

      if (!model) {
        // Model not in store, fetch it and add to cache (no UI state change)
        logger.info('[ModelStore] Model not in store, fetching:', modelId);
        await get().updateModelInCache(api, modelId);
      } else {
        // Model exists, refresh it
        logger.info('[ModelStore] Model exists, refreshing it:', modelId);
        await get().updateModelInCache(api, modelId);
      }
    },

    handleModelDeleted: async (api, message) => {
      const { modelId } = message;

      // look locally first, we might be the originator of the delete
      const existing = get().models.get(modelId);
      if (!existing) {
        logger.info('[ModelStore] Model not in store, skipping delete handling:', {
          modelId,
        });
        return;
      }

      logger.debug('[ModelStore] Handling model deleted:', {
        modelId,
      });

      // Wait for model to not exist (confirm deletion)
      const modelDeleted = await get().waitForModelNotExists(
        api,
        modelId
      );

      if (modelDeleted) {
        logger.info('[ModelStore] Model successfully confirmed as deleted:', {
          modelId
        });
      } else {
        logger.warn('[ModelStore] Model still exists after retries, proceeding anyway:', {
          modelId
        });
      }

      // Check if model exists in store
      const model = get().models.get(modelId);

      if (model) {
        // Model exists, remove it from cache (no UI state change)
        logger.info('[ModelStore] Model exists, removing from cache:', modelId);
        await get().removeModelFromCache(api, modelId);
      } else {
        // Model not in store, nothing to do
        logger.info('[ModelStore] Model not in store, nothing to do:', modelId);
      }
    },

    handleModelPublished: async (api, message) => {
      const { modelId, versionId, checkpointId } = message;

      // look locally first, we might be the originator of the publish
      const existing = get().models.get(modelId);
      if (existing) {
        const existingVersion = existing.versions?.find(v => v.id === versionId);
        if (existingVersion && existingVersion.status === 'published') {
          logger.info('[ModelStore] Model version already published in store, skipping publish handling:', {
            modelId,
            versionId,
          });
          return;
        }
      }

      logger.debug('[ModelStore] Handling model published:', {
        modelId,
        versionId,
        checkpointId
      });

      // Wait for version to get to published state
      const finalStatuses = ['published'];

      const versionReady = await get().waitForVersionStatus(
        api,
        modelId,
        versionId,
        finalStatuses
      );

      if (!versionReady) {
        logger.warn('[ModelStore] Version still not published after retries:', {
          versionId,
          expectedStatuses: finalStatuses
        });
        // Continue anyway - we'll get the best data available
      } else {
        logger.info('[ModelStore] Version reached published state, proceeding with update');
      }

      // Check if model exists in store
      const model = get().models.get(modelId);

      if (!model) {
        // Model not in store, fetch it and add to cache (no UI state change)
        logger.info('[ModelStore] Model not in store, fetching:', modelId);
        await get().updateModelInCache(api, modelId);
      } else {
        // Model exists, refresh version only
        logger.info('[ModelStore] Model exists, refreshing it:', modelId);
        await get().updateModelInCache(api, modelId);
      }

      // Track onboarding milestone: model published
      const { getPreference, setPreference } = useProfileStore.getState();
      const hasPublishedMilestone = getPreference(PREFERENCE_KEYS.ONBOARDING_MILESTONE_MODEL_PUBLISHED);
      if (hasPublishedMilestone !== 'true') {
        setPreference(api, PREFERENCE_KEYS.ONBOARDING_MILESTONE_MODEL_PUBLISHED, 'true');
      }

      // Add attention indicator if this model is not currently selected
      if (modelId !== get().currentModelId) {
        get().addModelAttention(modelId);
      }
    },

    handleModelChange: async (api, message) => {
      const { modelId, name, description, handle, picture, visibility } = message;

      // we could look to see if we have these changes already, but it's probably not worth it

      logger.debug('[ModelStore] Handling model change:', {
        modelId,
        name,
        description,
        handle,
        picture,
        visibility
      });

      // Check if model exists in store
      const model = get().models.get(modelId);

      if (!model) {
        // Model not in store, fetch it and add to cache (no UI state change)
        logger.info('[ModelStore] Model not in store, fetching:', modelId);
        await get().updateModelInCache(api, modelId);
      } else {
        // Model exists, update local cache with any values passed in the event as long as they are not undefined
        const updatedModel = produce(model, (draft) => {
          if (name) draft.name = name;
          if (description) draft.description = description;
          if (handle) draft.handle = handle;
          if (picture) draft.picture = picture;
          if (visibility) draft.visibility = visibility as Model['visibility'];
        });

        set((state) => ({
          models: new Map(state.models).set(modelId, updatedModel),
        }));

        logger.info('[ModelStore] Model exists, refreshing it:', modelId);
        await get().updateModelInCache(api, modelId);
      }
    },    
    
    handleModelVersionCreated: async (api, message) => {
      const { modelId, versionId } = message;

      // look locally first, we might be the originator of the create
      const existing = get().models.get(modelId);
      if (existing) {
        const existingVersion = existing.versions?.find(v => v.id === versionId);
        if (existingVersion) {
          logger.info('[ModelStore] Model version already exists in store, skipping create handling:', {
            modelId,
            versionId,
          });
          return;
        }
      }

      logger.debug('[ModelStore] Handling model created:', {
        modelId,
        versionId
      });

      // Wait for model version to exist
      const versionReady = await get().waitForModelVersionExists(
        api,
        modelId,
        versionId
      );

      if (!versionReady) {
        logger.warn('[ModelStore] Model version still not there after retries:', {
          modelId,
          versionId
        });
        // Continue anyway - we'll get the best data available
      } else {
        logger.info('[ModelStore] Model version exists, proceeding with update');
      }

      // Check if model exists in store
      const model = get().models.get(modelId);

      if (!model) {
        // Model not in store, fetch it and add to cache (no UI state change)
        logger.info('[ModelStore] Model not in store, fetching:', modelId);
        await get().updateModelInCache(api, modelId);
      } else {
        // Model exists, refresh it
        logger.info('[ModelStore] Model exists, refreshing it:', modelId);
        await get().updateModelInCache(api, modelId);
      }
    },

    handleModelVersionDeleted: async (api, message) => {
      const { modelId, versionId } = message;

      // look locally first, we might be the originator of the delete
      const existing = get().models.get(modelId);
      if (existing) {
        const existingVersion = existing.versions?.find(v => v.id === versionId);
        if (!existingVersion) {
          logger.info('[ModelStore] Model version not in store, skipping delete handling:', {
            modelId,
            versionId,
          });
          return;
        }
      }

      logger.debug('[ModelStore] Handling model version deleted:', {
        modelId,
        versionId,
      });

      // Wait for model version to not exist (confirm deletion)
      const versionDeleted = await get().waitForModelVersionNotExists(
        api,
        modelId,
        versionId
      );

      if (versionDeleted) {
        logger.info('[ModelStore] Model version successfully confirmed as deleted:', {
          modelId,
          versionId
        });
      } else {
        logger.warn('[ModelStore] Model version still exists after retries, proceeding anyway:', {
          modelId,
          versionId
        });
      }

      // Check if model exists in store
      const model = get().models.get(modelId);

      if (model) {
        // Model exists, see if version exists and remove it from cache (no UI state change)
        const versionExists = model.versions?.some(v => v.id === versionId);
        if (versionExists) {
          const updatedModel = produce(model, (draft) => {
            if (draft.versions) {
              draft.versions = draft.versions.filter(v => v.id !== versionId);
            }
          });
          set((state) => ({
            models: new Map(state.models).set(modelId, updatedModel),
          }));
          logger.info('[ModelStore] Model version existed, removed from cache:', {
            modelId,
            versionId
          });
        } else {
          logger.info('[ModelStore] Model version not in store, nothing to do:', {
            modelId,
            versionId
          });
        }
      } else {
        // Model not in store, nothing to do
        logger.info('[ModelStore] Model not in store, nothing to do:', modelId);
      }
    },

    handleCheckpointCreated: async (api, message) => {
      const { modelId, versionId, checkpointId } = message;

      // look locally first, we might be the originator of the create
      const existing = get().models.get(modelId);
      if (existing) {
        const existingVersion = existing.versions?.find(v => v.id === versionId);
        if (existingVersion) {
          const existingCheckpoint = existingVersion.checkpoints?.find(cp => cp.id === checkpointId);
          if (existingCheckpoint) {
            // If checkpoint is still starting, let it update
            if (existingCheckpoint.operation?.status === 'starting') {
              logger.info('[ModelStore] Checkpoint exists but still starting, will refresh:', {
                modelId,
                versionId,
                checkpointId,
                currentStatus: existingCheckpoint.operation?.status
              });
            } else {
              logger.info('[ModelStore] Checkpoint already exists with operation running, skipping:', {
                modelId,
                versionId,
                checkpointId,
                operationStatus: existingCheckpoint.operation?.status
              });
              return;
            }
          }
        }
      }

      logger.debug('[ModelStore] Handling checkpoint created:', {
        modelId,
        versionId,
        checkpointId
      });

      // Wait for model version to exist
      const checkpointReady = await get().waitForCheckpointExists(
        api,
        modelId,
        versionId,
        checkpointId
      );

      if (!checkpointReady) {
        logger.warn('[ModelStore] Checkpoint still not there after retries:', {
          modelId,
          versionId,
          checkpointId
        });
        // Continue anyway - we'll get the best data available
      } else {
        logger.info('[ModelStore] Checkpoint exists, proceeding with update');
      }

      // Check if model exists in store
      const model = get().models.get(modelId);

      if (!model) {
        // Model not in store, fetch it and add to cache (no UI state change)
        logger.info('[ModelStore] Model not in store, fetching:', modelId);
        await get().updateModelInCache(api, modelId);
      } else {
        // Model exists, refresh it
        logger.info('[ModelStore] Model exists, refreshing it:', modelId);
        await get().updateModelInCache(api, modelId);
      }

      // Add attention indicator if this model is not currently selected
      // Checkpoint created means operation is starting (testing/training)
      if (modelId !== get().currentModelId) {
        get().addModelAttention(modelId);
      }
    },

  }))
);